import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, ShoppingBag, Package, Truck, RotateCcw, IndianRupee, Layers, RefreshCw, Crown, ArrowLeftRight, Gift, CreditCard, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate, imgUrl } from "@/lib/api";
import { PageHeader, Pager, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CHANNEL_LABELS = { amazon: "Amazon", flipkart: "Flipkart" };

async function sha256(value) {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function ChoosePlanPrompt({ onChoose }) {
  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50 p-8 text-center" data-testid="mp-choose-plan-prompt">
      <Gift size={30} className="mx-auto text-[#FF6B00]" />
      <h3 className="mt-3 text-lg font-bold text-[#0F2440]">Choose your Marketplace plan to start selling</h3>
      <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-slate-600">Pick 25 / 50 / 75 / 100 products — 3-month prepaid first term (minimum), then monthly per plan. Swap products anytime at no extra charge.</p>
      <Button data-testid="go-myplan-btn" onClick={onChoose} className="mt-4 bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">Choose Marketplace Plan</Button>
    </div>
  );
}

const PLAN_FEATURES = ["Marketplace product slots", "Amazon", "Flipkart", "Product swapping (free)", "Orders & tracking", "Sales dashboard", "3-month minimum first term (prepaid)"];

function loadRazorpay() {
  return new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

async function openRazorpayCheckout(co) {
  const loaded = await loadRazorpay();
  if (!loaded) { toast.error("Could not load the payment gateway — check your connection"); return false; }
  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: co.key_id, amount: co.amount, currency: "INR", name: "DropPartner Marketplace",
      description: co.description, order_id: co.order_id,
      handler: async (resp) => {
        try {
          await api.post("/agent/marketplace/billing/verify", {
            purchase_id: co.purchase_id, razorpay_order_id: co.order_id,
            razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature,
          });
          resolve(true);
        } catch (e) { toast.error(errMsg(e)); resolve(false); }
      },
      modal: { ondismiss: () => resolve(false) },
      theme: { color: "#0F2440" },
    });
    rzp.open();
  });
}

function PlanTab({ plansData, myPlan, onChanged }) {
  const [payBusy, setPayBusy] = useState(false);
  const subscribe = useMutation({
    mutationFn: async (plan_id) => (await api.post("/agent/marketplace/plans/subscribe", { plan_id })).data,
    onSuccess: async (d) => {
      const co = d.checkout;
      if (!co) { toast.success("Plan activated"); onChanged(); return; }
      if (co.mode === "mock") {
        const paymentId = `pay_mock_${crypto.randomUUID().slice(0, 8)}`;
        const signature = await sha256(`${co.order_id}|${paymentId}`);
        await api.post("/agent/marketplace/billing/verify", { purchase_id: co.purchase_id, razorpay_order_id: co.order_id, razorpay_payment_id: paymentId, razorpay_signature: signature });
        toast.success("Payment successful — your 3-month term is active");
        onChanged();
      } else {
        const ok = await openRazorpayCheckout(co);
        if (ok) { toast.success("Payment successful — your 3-month term is active"); onChanged(); }
      }
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const change = useMutation({
    mutationFn: async (plan_id) => (await api.post("/agent/marketplace/plans/change", { plan_id })).data,
    onSuccess: (d) => { toast.success(`Plan ${d.direction}d — no extra charge for the change`); onChanged(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const payNow = async () => {
    setPayBusy(true);
    try {
      const { data: co } = await api.post("/agent/marketplace/billing/checkout", {});
      if (co.mode === "mock") {
        const paymentId = `pay_mock_${crypto.randomUUID().slice(0, 8)}`;
        const signature = await sha256(`${co.order_id}|${paymentId}`);
        await api.post("/agent/marketplace/billing/verify", { purchase_id: co.purchase_id, razorpay_order_id: co.order_id, razorpay_payment_id: paymentId, razorpay_signature: signature });
        toast.success("Payment successful — subscription active");
        onChanged();
      } else {
        const ok = await openRazorpayCheckout(co);
        if (ok) { toast.success("Payment successful — subscription active"); onChanged(); }
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setPayBusy(false); }
  };

  const sub = myPlan?.subscription;
  const plans = plansData?.plans || [];
  const isTrial = sub?.subscription_status === "TRIAL";
  const payDue = sub && ["pending", "failed"].includes(sub.payment_status) && ["ACTIVE", "PAST_DUE"].includes(sub.subscription_status);

  return (
    <div data-testid="mp-plan-tab">
      {sub && (
        <div className="mb-5 grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="mp-current-plan">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Marketplace Plan</p>
              <StatusBadge status={sub.subscription_status} />
            </div>
            <p className="mt-1 text-xl font-extrabold text-[#0F2440]" data-testid="mp-plan-name">{sub.plan_snapshot.name} <span className="text-sm font-semibold text-slate-500">· {fmtINR(sub.price)}/month</span></p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {[["Used", `${sub.usage.used} / ${sub.usage.limit}`, "mp-used"], ["Available", sub.usage.available, "mp-available"], ["Unique products", sub.usage.unique_products, "mp-unique"]].map(([l, v, tid]) => (
                <div key={l} className="rounded-lg bg-slate-50 p-2">
                  <p className="text-[9px] uppercase tracking-wider text-slate-400">{l}</p>
                  <p className="text-base font-extrabold text-[#0F2440]" data-testid={tid}>{v}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-between text-[11px] text-slate-500">
              <span>Amazon: <b className="text-[#0F2440]">{sub.usage.amazon}</b></span>
              <span>Flipkart: <b className="text-[#0F2440]">{sub.usage.flipkart}</b></span>
              <span className="italic">same product on both counts once</span>
            </div>
          </div>
          <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="mp-trial-card">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Billing</p>
            {isTrial ? (
              <div className="mt-2 space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Trial</span><span className="font-extrabold text-emerald-600" data-testid="trial-label">FREE</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Trial ends</span><span className="font-semibold text-[#0F2440]" data-testid="trial-end">{fmtDate(sub.trial_end)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Billing starts</span><span className="font-semibold text-[#0F2440]" data-testid="billing-start">{fmtDate(sub.billing_start)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">After trial</span><span className="font-semibold text-[#E85D04]">{fmtINR(sub.price)}/month</span></div>
              </div>
            ) : (
              <div className="mt-2 space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Subscription</span><StatusBadge status={sub.subscription_status} /></div>
                <div className="flex justify-between"><span className="text-slate-500">Payment</span><StatusBadge status={sub.payment_status} /></div>
                <div className="flex justify-between"><span className="text-slate-500">Next billing</span><span className="font-semibold text-[#0F2440]">{sub.next_billing_date ? fmtDate(sub.next_billing_date) : "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-semibold text-[#E85D04]">{fmtINR(sub.price)}/month</span></div>
              </div>
            )}
            {sub.subscription_status === "PENDING_PAYMENT" && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="activation-payment-card">
                <p className="flex items-center gap-1.5 text-xs font-bold text-amber-700"><AlertTriangle size={12} /> First-term payment pending</p>
                <p className="mt-1 text-[11px] text-amber-600">Pay {fmtINR(sub.price * (sub.term_months || 3))} to activate — {sub.term_months || 3} months prepaid, then {fmtINR(sub.price)}/month.</p>
                <Button size="sm" data-testid="pay-activate-btn" onClick={payNow} disabled={payBusy}
                  className="mt-2 bg-[#FF6B00] text-[11px] font-semibold text-white hover:bg-[#E85D04]">
                  <CreditCard size={11} /> {payBusy ? "Processing…" : "Pay & activate"}
                </Button>
              </div>
            )}
            {payDue && (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="payment-failed-card">
                <p className="flex items-center gap-1.5 text-xs font-bold text-rose-600"><AlertTriangle size={12} /> Marketplace subscription payment {sub.payment_status === "failed" ? "failed" : "due"}</p>
                <Button size="sm" data-testid="retry-payment-btn" onClick={payNow} disabled={payBusy}
                  className="mt-2 bg-[#FF6B00] text-[11px] font-semibold text-white hover:bg-[#E85D04]">
                  <CreditCard size={11} /> {payBusy ? "Processing…" : "Retry payment"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {!sub && (
        <p className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-center text-xs font-bold text-amber-700" data-testid="term-banner">
          FIRST TERM: 3 MONTHS PREPAID (MINIMUM) — then billed monthly per plan. Cancel anytime after the first term.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="mp-plan-grid">
        {plans.map((p) => {
          const isCurrent = sub?.plan_id === p.id;
          return (
            <div key={p.id} data-testid={`mp-plan-${p.slug}`} className={`rounded-xl border-2 bg-white p-5 shadow-sm ${isCurrent ? "border-[#FF6B00]" : "border-slate-100"}`}>
              <p className="text-base font-extrabold text-[#0F2440]">{p.name}</p>
              <p className="mt-1 text-2xl font-extrabold text-[#E85D04]">{fmtINR(p.monthly_price)}<span className="text-xs font-semibold text-slate-400">/month</span></p>
              <p className="mt-0.5 text-[11px] font-semibold text-amber-600">First term: {fmtINR(p.monthly_price * (p.trial_months || 3))} prepaid ({p.trial_months || 3} months min)</p>
              <ul className="mt-3 space-y-1 text-[11px] text-slate-600">
                <li className="font-bold text-[#0F2440]">{p.item_limit} Marketplace products</li>
                {PLAN_FEATURES.slice(1).map((f) => <li key={f}>✓ {f}</li>)}
              </ul>
              {sub ? (
                isCurrent ? (
                  <Button size="sm" disabled className="mt-4 w-full bg-slate-100 text-[11px] font-bold text-slate-400">Current plan</Button>
                ) : (
                  <Button size="sm" data-testid={`change-plan-${p.slug}`} onClick={() => change.mutate(p.id)} disabled={change.isPending}
                    className="mt-4 w-full bg-[#1D5BD8] text-[11px] font-semibold text-white hover:bg-[#1a4fc0]">
                    {p.item_limit > sub.plan_snapshot.item_limit ? "Upgrade" : "Downgrade"} to {p.item_limit}
                  </Button>
                )
              ) : (
                <Button size="sm" data-testid={`subscribe-${p.slug}`} onClick={() => subscribe.mutate(p.id)} disabled={subscribe.isPending}
                  className="mt-4 w-full bg-[#FF6B00] text-[11px] font-semibold text-white hover:bg-[#E85D04]">
                  {subscribe.isPending ? "Preparing payment…" : `Subscribe & pay ${fmtINR(p.monthly_price * (p.trial_months || 3))}`}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {sub && (myPlan?.subscription?.billing_events?.length > 0) && (
        <div className="mt-5 rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="billing-history">
          <p className="mb-2 text-xs font-bold text-[#0F2440]">Billing history</p>
          <div className="space-y-1">
            {myPlan.subscription.billing_events.map((e) => (
              <div key={e.id} className="flex justify-between border-b border-slate-50 py-1.5 text-[11px]">
                <span className="capitalize text-slate-600">{e.type.replace(/_/g, " ")}</span>
                <span className="text-slate-400">{e.amount ? fmtINR(e.amount) : "—"} · {fmtDate(e.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SwapDialog({ current, onClose, onDone }) {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [channel, setChannel] = useState("amazon");
  const { data: catalog } = useQuery({
    queryKey: ["mp-swap-catalog", search],
    queryFn: async () => (await api.get(`/agent/marketplace/catalog?search=${encodeURIComponent(search)}&limit=12`)).data,
    enabled: !!current,
  });
  const swap = useMutation({
    mutationFn: async () => (await api.post("/agent/marketplace/swap", {
      old_product_id: current.product_id, new_product_id: picked.id, marketplace: channel,
      suggested_price: picked.suggested_price, reason: "swap",
    })).data,
    onSuccess: () => { toast.success("Product swapped — no extra charge"); onDone(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!current) return null;
  return (
    <Dialog open={!!current} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-slate-200 bg-white text-[#0F2440]">
        <DialogHeader><DialogTitle>Replace product — free swap</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-slate-50 p-3 text-xs">
          <p className="text-slate-500">Releasing</p>
          <p className="font-semibold text-[#0F2440]">{current.product_name} <span className="font-mono text-[10px] text-slate-400">{current.sku}</span></p>
          <p className="mt-1 text-[10px] text-slate-400">Your slot frees immediately · order/sales history is preserved · admin decides what happens to the live marketplace listing.</p>
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">New product goes to</p>
          <div className="flex gap-2">
            {["amazon", "flipkart"].map((ch) => (
              <button key={ch} data-testid={`swap-channel-${ch}`} onClick={() => setChannel(ch)}
                className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold ${channel === ch ? "border-[#FF6B00] bg-orange-50 text-[#E85D04]" : "border-slate-200 text-slate-500"}`}>
                {CHANNEL_LABELS[ch]}
              </button>
            ))}
          </div>
        </div>
        <Input data-testid="swap-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search replacement product…" className="border-slate-200 text-xs" />
        <div className="max-h-56 space-y-1.5 overflow-y-auto">
          {(catalog?.items || []).filter((p) => p.id !== current.product_id).map((p) => (
            <button key={p.id} data-testid={`swap-pick-${p.sku}`} onClick={() => setPicked(p)}
              className={`flex w-full items-center gap-2.5 rounded-lg border p-2 text-left ${picked?.id === p.id ? "border-[#FF6B00] bg-orange-50" : "border-slate-100"}`}>
              {(p.images || [])[0]
                ? <img src={imgUrl(p.images[0])} alt={p.name} className="h-9 w-9 rounded object-cover" />
                : <div className="flex h-9 w-9 items-center justify-center rounded bg-slate-100"><Package size={12} className="text-slate-300" /></div>}
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold">{p.name}</p>
                <p className="font-mono text-[9px] text-slate-400">{p.sku} · slots {p.slots_used}/{p.slots_total}</p>
              </div>
            </button>
          ))}
        </div>
        <Button data-testid="swap-confirm-btn" onClick={() => swap.mutate()} disabled={!picked || swap.isPending}
          className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
          <ArrowLeftRight size={13} /> {swap.isPending ? "Swapping…" : "Confirm swap — no charge"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function MyProductsTab({ data, onChanged }) {
  const [releaseFor, setReleaseFor] = useState(null);
  const [swapFor, setSwapFor] = useState(null);
  const release = useMutation({
    mutationFn: async (pid) => (await api.post(`/agent/marketplace/products/${pid}/release`, { reason: "agent_release" })).data,
    onSuccess: () => { toast.success("Product released — slot freed. History preserved."); setReleaseFor(null); onChanged(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const sub = data?.subscription;
  const items = data?.items || [];
  const hasLiveListing = (g) => Object.values(g.marketplaces).some((m) => ["ACTIVE", "PUBLISHING"].includes(m.listing_status));
  return (
    <div data-testid="mp-my-products">
      {sub && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="mp-usage-cards">
          {[["Plan", sub.plan_snapshot.name, "mpu-plan"], ["Used", `${sub.usage.used} / ${sub.usage.limit}`, "mpu-used"],
            ["Available", sub.usage.available, "mpu-available"], ["Amazon", sub.usage.amazon, "mpu-amazon"], ["Flipkart", sub.usage.flipkart, "mpu-flipkart"]].map(([l, v, tid]) => (
            <div key={l} className="rounded-xl border border-slate-100 bg-white p-3 text-center shadow-sm">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{l}</p>
              <p className="mt-0.5 text-sm font-extrabold text-[#0F2440]" data-testid={tid}>{v}</p>
            </div>
          ))}
        </div>
      )}
      {!items.length ? <EmptyState title="No marketplace products yet" sub="Select products from the Select Products tab — they appear here with listing, order and sales status." /> : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="mp-my-products-grid">
          {items.map((g) => (
            <div key={g.product_id} data-testid={`my-mp-product-${g.sku}`} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex gap-3">
                {g.image
                  ? <img src={imgUrl(g.image)} alt={g.product_name} className="h-14 w-14 rounded-lg object-cover" />
                  : <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-slate-100"><Package size={16} className="text-slate-300" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#0F2440]">{g.product_name}</p>
                  <p className="font-mono text-[10px] text-slate-400">{g.sku}</p>
                  <p className="mt-0.5 text-[10px] text-slate-500">Orders: <b>{g.orders}</b> · Sales: <b>{fmtINR(g.sales)}</b></p>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {Object.entries(g.marketplaces).map(([ch, m]) => (
                  <div key={ch} className="rounded-lg border border-slate-100 px-3 py-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-600">{CHANNEL_LABELS[ch]}</span>
                      <span className="flex items-center gap-2">
                        <StatusBadge status={m.status} />
                        {m.listing_status && <span className="text-[9px] font-semibold uppercase text-slate-400" data-testid={`listing-status-${ch}-${g.sku}`}>{m.listing_status}</span>}
                      </span>
                    </div>
                    {m.status === "REJECTED" && m.review_note && (
                      <p data-testid={`reject-reason-${ch}-${g.sku}`} className="mt-1 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] text-rose-600">Reason: {m.review_note}</p>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" data-testid={`swap-${g.sku}`} onClick={() => setSwapFor(g)}
                  className="flex-1 border-[#1D5BD8] text-[11px] font-semibold text-[#1D5BD8] hover:bg-blue-50">
                  <ArrowLeftRight size={11} /> Replace product
                </Button>
                <Button size="sm" variant="outline" data-testid={`release-${g.sku}`} onClick={() => setReleaseFor(g)}
                  className="border-rose-200 text-[11px] font-semibold text-rose-500 hover:bg-rose-50">Release</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SwapDialog current={swapFor} onClose={() => setSwapFor(null)} onDone={() => { setSwapFor(null); onChanged(); }} />

      <Dialog open={!!releaseFor} onOpenChange={() => setReleaseFor(null)}>
        <DialogContent className="border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>Release {releaseFor?.product_name}?</DialogTitle></DialogHeader>
          {releaseFor && hasLiveListing(releaseFor) && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="release-warning">
              <AlertTriangle size={12} className="mr-1 inline" /> This product has live Amazon/Flipkart listings. Your slot frees immediately and
              admin decides whether the marketplace listing is deactivated, removed or kept. Nothing is deleted automatically.
            </p>
          )}
          <p className="text-xs text-slate-500">Your orders, sales, shipments, returns and sync history for this product stay available forever. Releasing is free — you can pick a new product right after.</p>
          <Button data-testid="release-confirm-btn" onClick={() => release.mutate(releaseFor.product_id)} disabled={release.isPending}
            className="w-full bg-rose-500 font-semibold text-white hover:bg-rose-600">
            {release.isPending ? "Releasing…" : "Confirm release"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PriceSelectDialog({ product, channel, onClose, onDone }) {
  const [price, setPrice] = useState(product?.suggested_price || "");
  const sel = useMutation({
    mutationFn: async () => (await api.post("/agent/marketplace/select", { product_id: product.id, marketplace: channel, suggested_price: Number(price) })).data,
    onSuccess: (d) => { toast.success(d.already ? "Already selected" : `Selected for ${CHANNEL_LABELS[channel]} — sent for admin review`); onDone(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!product) return null;
  return (
    <Dialog open={!!product} onOpenChange={onClose}>
      <DialogContent className="border-slate-200 bg-white text-[#0F2440]">
        <DialogHeader><DialogTitle>Select for {CHANNEL_LABELS[channel]}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3">
          <img src={imgUrl((product.images || [])[0])} alt="" className="h-12 w-12 rounded-lg object-cover" />
          <div>
            <p className="text-sm font-semibold">{product.name}</p>
            <p className="font-mono text-[10px] text-slate-500">{product.sku} · base {fmtINR(product.agent_cost)}</p>
          </div>
        </div>
        <div>
          <p className="text-xs text-slate-500">Your suggested marketplace price (min {fmtINR(product.min_price || 0)})</p>
          <Input data-testid="mp-price-input" type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 border-slate-200 bg-white" />
        </div>
        <Button data-testid="mp-select-confirm-btn" onClick={() => sel.mutate()} disabled={sel.isPending || !price}
          className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
          {sel.isPending ? "Selecting…" : `Select for ${CHANNEL_LABELS[channel]}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ListingsTable({ items, testid }) {
  if (!items?.length) return <EmptyState title="No listings yet" sub="Products appear here after admin publishes your approved selections." />;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
      <table className="w-full text-left text-xs" data-testid={testid}>
        <thead>
          <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
            <th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Listing ID</th>
            <th className="px-4 py-3">Base Price</th><th className="px-4 py-3">Marketplace Price</th><th className="px-4 py-3">Stock</th>
            <th className="px-4 py-3">Status</th><th className="px-4 py-3">Last Sync</th>
          </tr>
        </thead>
        <tbody>
          {items.map((l) => (
            <tr key={l.id} data-testid={`listing-row-${l.id}`} className="border-b border-slate-50">
              <td className="px-4 py-2.5 font-medium text-[#0F2440]">{l.product_name}</td>
              <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{l.sku}</td>
              <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{l.marketplace_listing_id || l.marketplace_sku || "—"}</td>
              <td className="px-4 py-2.5 text-slate-500">{l.base_price ? fmtINR(l.base_price) : "—"}</td>
              <td className="px-4 py-2.5 font-semibold">{fmtINR(l.marketplace_price || 0)}</td>
              <td className="px-4 py-2.5">{l.marketplace_quantity ?? "—"}</td>
              <td className="px-4 py-2.5"><StatusBadge status={l.listing_status} />{l.error_message && <p className="mt-0.5 text-[9px] text-rose-500">{l.error_message}</p>}</td>
              <td className="px-4 py-2.5 text-slate-500">{l.last_synced_at ? fmtDate(l.last_synced_at) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Marketplace() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectFor, setSelectFor] = useState(null);
  const [selectChannel, setSelectChannel] = useState("amazon");
  const [tab, setTab] = useState("select");

  const { data: overview } = useQuery({ queryKey: ["mp-overview"], queryFn: async () => (await api.get("/agent/marketplace/overview")).data });
  const { data: plansData } = useQuery({ queryKey: ["mp-plans"], queryFn: async () => (await api.get("/agent/marketplace/plans")).data });
  const { data: myPlan } = useQuery({ queryKey: ["mp-my-plan"], queryFn: async () => (await api.get("/agent/marketplace/my-plan")).data });
  const { data: myProducts } = useQuery({ queryKey: ["mp-my-products"], queryFn: async () => (await api.get("/agent/marketplace/my-products")).data });
  const planChanged = () => ["mp-plans", "mp-my-plan", "mp-my-products", "mp-catalog", "mp-overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const mpSub = myPlan?.subscription;
  const { data: catalog } = useQuery({
    queryKey: ["mp-catalog", search, page],
    queryFn: async () => (await api.get(`/agent/marketplace/catalog?search=${encodeURIComponent(search)}&page=${page}`)).data,
  });
  const { data: amazonListings } = useQuery({ queryKey: ["mp-listings", "amazon"], queryFn: async () => (await api.get("/agent/marketplace/listings?marketplace=amazon")).data });
  const { data: flipkartListings } = useQuery({ queryKey: ["mp-listings", "flipkart"], queryFn: async () => (await api.get("/agent/marketplace/listings?marketplace=flipkart")).data });
  const { data: orders } = useQuery({ queryKey: ["mp-orders"], queryFn: async () => (await api.get("/agent/marketplace/orders")).data });
  const { data: shipments } = useQuery({ queryKey: ["mp-shipments"], queryFn: async () => (await api.get("/agent/marketplace/shipments")).data });
  const { data: returns } = useQuery({ queryKey: ["mp-returns"], queryFn: async () => (await api.get("/agent/marketplace/returns")).data });
  const { data: sales } = useQuery({ queryKey: ["mp-sales"], queryFn: async () => (await api.get("/agent/marketplace/sales")).data });

  const deselect = useMutation({
    mutationFn: async (sid) => api.delete(`/agent/marketplace/selections/${sid}`),
    onSuccess: () => { toast.success("Selection removed"); ["mp-catalog", "mp-overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const cards = [
    { icon: Layers, label: "Active listings", value: overview?.active_listings ?? 0, testid: "mp-active-listings" },
    { icon: Package, label: "Pending", value: overview?.pending_listings ?? 0, testid: "mp-pending-listings" },
    { icon: ShoppingBag, label: "Orders", value: overview?.orders ?? 0, testid: "mp-orders-count" },
    { icon: Package, label: "Units sold", value: overview?.units_sold ?? 0, testid: "mp-units-sold" },
    { icon: IndianRupee, label: "Sales", value: fmtINR(overview?.sales ?? 0), testid: "mp-sales-value" },
    { icon: Truck, label: "Pending shipments", value: overview?.pending_shipments ?? 0, testid: "mp-pending-ship" },
    { icon: Truck, label: "Delivered", value: overview?.delivered ?? 0, testid: "mp-delivered" },
    { icon: RotateCcw, label: "Returns", value: overview?.returns ?? 0, testid: "mp-returns-count" },
  ];

  return (
    <div data-testid="my-marketplace-page">
      <PageHeader title="My Marketplace" sub="Select products for Amazon & Flipkart — IndiaFast admin reviews and publishes them. Track your listings, orders, shipments, returns and sales here." testid="my-marketplace-title" />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className="mt-1 text-lg font-extrabold text-[#0F2440]">{c.value}</p>
          </div>
        ))}
      </div>
      {overview?.last_sync && (
        <p className="mb-4 text-[10px] text-slate-400" data-testid="mp-last-sync">Last synced: {fmtDate(overview.last_sync)} · {overview.last_sync_status}</p>
      )}

      {mpSub && (
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-slate-100 bg-white px-4 py-2.5 text-[11px] shadow-sm" data-testid="mp-plan-strip">
          <Crown size={12} className="text-[#FF6B00]" />
          <span className="font-bold text-[#0F2440]">{mpSub.plan_snapshot.name}</span>
          <span className="text-slate-500">Used: <b className="text-[#0F2440]">{mpSub.usage.used}/{mpSub.usage.limit}</b></span>
          <span className="text-slate-500">Available: <b className="text-emerald-600">{mpSub.usage.available}</b></span>
          <span className="text-slate-500">Amazon: <b className="text-[#0F2440]">{mpSub.usage.amazon}</b></span>
          <span className="text-slate-500">Flipkart: <b className="text-[#0F2440]">{mpSub.usage.flipkart}</b></span>
          <span className="text-slate-500">Unique: <b className="text-[#0F2440]">{mpSub.usage.unique_products}</b></span>
          <StatusBadge status={mpSub.subscription_status} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap border border-slate-200 bg-white">
          <TabsTrigger value="select" data-testid="tab-mp-select">Select Products</TabsTrigger>
          <TabsTrigger value="myproducts" data-testid="tab-mp-myproducts">My Products</TabsTrigger>
          <TabsTrigger value="myplan" data-testid="tab-mp-myplan">My Plan</TabsTrigger>
          <TabsTrigger value="amazon" data-testid="tab-mp-amazon">Amazon</TabsTrigger>
          <TabsTrigger value="flipkart" data-testid="tab-mp-flipkart">Flipkart</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-mp-orders">Orders</TabsTrigger>
          <TabsTrigger value="shipments" data-testid="tab-mp-shipments">Shipments</TabsTrigger>
          <TabsTrigger value="returns" data-testid="tab-mp-returns">Returns</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-mp-sales">Sales</TabsTrigger>
        </TabsList>

        <TabsContent value="myplan" className="mt-4">
          <PlanTab plansData={plansData} myPlan={myPlan} onChanged={planChanged} />
        </TabsContent>

        <TabsContent value="myproducts" className="mt-4">
          <MyProductsTab data={myProducts} onChanged={planChanged} />
        </TabsContent>

        <TabsContent value="select" className="mt-4">
          {plansData && !plansData.subscription ? <ChoosePlanPrompt onChoose={() => setTab("myplan")} /> : (
          <>
          <div className="relative mb-4 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="mp-catalog-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search products…" className="border-slate-200 bg-white pl-9 text-[#0F2440]" />
          </div>
          {!catalog?.items?.length ? <EmptyState title="No products available" sub="All products are either fully allocated (5/5 agents) or none match your search." /> : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="mp-catalog-grid">
              {catalog.items.map((p) => (
                <div key={p.id} data-testid={`mp-product-${p.sku}`} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="flex gap-3">
                    {(p.images || [])[0]
                      ? <img src={imgUrl(p.images[0])} alt={p.name} className="h-14 w-14 rounded-lg object-cover" />
                      : <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-slate-100"><Package size={16} className="text-slate-300" /></div>}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[#0F2440]">{p.name}</p>
                      <p className="font-mono text-[10px] text-slate-500">{p.sku}</p>
                      <div className="mt-1 flex items-center gap-2 text-[10px]">
                        <span className="text-slate-500">Base {fmtINR(p.agent_cost)}</span>
                        <span data-testid={`slots-${p.sku}`} className={`rounded-full px-1.5 py-0.5 font-semibold ${p.slots_used >= 4 ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
                          Slots {p.slots_used}/{p.slots_total}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {["amazon", "flipkart"].map((ch) => {
                      const sel = p.selections?.[ch];
                      return (
                        <div key={ch} className="rounded-lg border border-slate-100 px-3 py-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold text-slate-600">{CHANNEL_LABELS[ch]}</span>
                            {sel ? (
                              <span className="flex items-center gap-2">
                                <StatusBadge status={sel.status} />
                                {["SELECTED", "REJECTED", "FAILED"].includes(sel.status) && (
                                  <button data-testid={`deselect-${ch}-${p.sku}`} onClick={() => deselect.mutate(sel.id)}
                                    className="text-[10px] font-semibold text-rose-500 hover:underline">Remove</button>
                                )}
                              </span>
                            ) : (
                              <Button data-testid={`select-${ch}-${p.sku}`} size="sm" onClick={() => { setSelectFor(p); setSelectChannel(ch); }}
                                className="h-6 bg-[#1D5BD8] px-2.5 text-[10px] font-semibold text-white hover:bg-[#1a4fc0]">Select</Button>
                            )}
                          </div>
                          {sel?.status === "REJECTED" && sel.review_note && (
                            <p data-testid={`catalog-reject-reason-${ch}-${p.sku}`} className="mt-1 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] text-rose-600">Reason: {sel.review_note}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          {catalog && <Pager page={catalog.page} pages={catalog.pages} onPage={setPage} />}
          </>
          )}
        </TabsContent>

        <TabsContent value="amazon" className="mt-4"><ListingsTable items={amazonListings?.items} testid="amazon-listings-table" /></TabsContent>
        <TabsContent value="flipkart" className="mt-4"><ListingsTable items={flipkartListings?.items} testid="flipkart-listings-table" /></TabsContent>

        <TabsContent value="orders" className="mt-4">
          {!orders?.items?.length ? <EmptyState title="No marketplace orders" sub="Orders appear here after sync once your listings receive them." /> : (
            <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
              <table className="w-full text-left text-xs" data-testid="mp-orders-table">
                <thead><tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3">Order ID</th><th className="px-4 py-3">Marketplace</th><th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Items</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th>
                </tr></thead>
                <tbody>
                  {orders.items.map((o) => (
                    <tr key={o.id} data-testid={`mp-order-${o.marketplace_order_id}`} className="border-b border-slate-50">
                      <td className="px-4 py-2.5 font-mono text-[10px] text-slate-600">{o.marketplace_order_id}</td>
                      <td className="px-4 py-2.5 capitalize">{o.marketplace}</td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(o.order_date)}</td>
                      <td className="px-4 py-2.5 text-slate-500">{(o.items || []).map((i) => `${i.marketplace_sku} ×${i.quantity}`).join(", ") || "—"}</td>
                      <td className="px-4 py-2.5 font-semibold">{fmtINR(o.total_amount)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={o.order_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="shipments" className="mt-4">
          {!shipments?.items?.length ? <EmptyState title="No shipments" sub="Shipment tracking syncs here from the marketplaces." /> : (
            <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
              <table className="w-full text-left text-xs" data-testid="mp-shipments-table">
                <thead><tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3">Order</th><th className="px-4 py-3">Tracking</th><th className="px-4 py-3">Courier</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last Sync</th>
                </tr></thead>
                <tbody>
                  {shipments.items.map((s) => (
                    <tr key={s.id} className="border-b border-slate-50">
                      <td className="px-4 py-2.5 font-mono text-[10px] text-slate-600">{s.order_id?.slice(0, 8)}</td>
                      <td className="px-4 py-2.5 font-mono text-[10px]">{s.tracking_number || "—"}</td>
                      <td className="px-4 py-2.5">{s.courier || "—"}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={s.shipment_status} /></td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(s.last_synced_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="returns" className="mt-4">
          {!returns?.items?.length ? <EmptyState title="No returns" sub="Marketplace returns appear here when synced." /> : (
            <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
              <table className="w-full text-left text-xs" data-testid="mp-returns-table">
                <thead><tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3">Return ID</th><th className="px-4 py-3">Marketplace</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Refund</th><th className="px-4 py-3">Status</th>
                </tr></thead>
                <tbody>
                  {returns.items.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50">
                      <td className="px-4 py-2.5 font-mono text-[10px] text-slate-600">{r.return_id}</td>
                      <td className="px-4 py-2.5 capitalize">{r.marketplace}</td>
                      <td className="px-4 py-2.5 text-slate-500">{r.return_reason || "—"}</td>
                      <td className="px-4 py-2.5">{r.refund_amount ? fmtINR(r.refund_amount) : "—"}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={r.return_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sales" className="mt-4">
          {!sales?.items?.length ? <EmptyState title="No sales yet" sub="Sales data syncs here after orders are placed on your listings." /> : (
            <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
              <table className="w-full text-left text-xs" data-testid="mp-sales-table">
                <thead><tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3">Date</th><th className="px-4 py-3">Marketplace</th><th className="px-4 py-3">Qty</th>
                  <th className="px-4 py-3">Gross</th><th className="px-4 py-3">Fees</th><th className="px-4 py-3">Refund</th><th className="px-4 py-3">Net</th>
                </tr></thead>
                <tbody>
                  {sales.items.map((s) => (
                    <tr key={s.id} className="border-b border-slate-50">
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(s.sale_date)}</td>
                      <td className="px-4 py-2.5 capitalize">{s.marketplace}</td>
                      <td className="px-4 py-2.5">{s.quantity}</td>
                      <td className="px-4 py-2.5 font-semibold">{fmtINR(s.gross_amount)}</td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtINR(s.fees || 0)}</td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtINR(s.refund_amount || 0)}</td>
                      <td className="px-4 py-2.5 font-semibold text-[#138808]">{fmtINR(s.net_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <PriceSelectDialog product={selectFor} channel={selectChannel} onClose={() => setSelectFor(null)}
        onDone={() => { setSelectFor(null); ["mp-catalog", "mp-overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); }} />
    </div>
  );
}
